import React, { FC, useLayoutEffect, useRef } from 'react';
import { Dismiss } from '@wix/wix-ui-icons-common';
import {
  InspirationalTemplate as Template,
  InspirationalTemplateAssetType,
} from '../../../domain/entities/inspirationalTemplate';

import s from './QuickViewModal.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { EditButton } from '../../EditButton/EditButton';
import { ViewButton } from '../../ViewButton/ViewButton';
import {
  BreakpointConfig,
  createBreakpoints,
  Picture,
} from '../../TemplatesGrid/components/Template/Picture/Picture';
import { QuickViewVideo } from '../QuickViewVideo/QuickViewVideo';

interface QuickViewModalProps {
  isOpen: boolean;
  onButtonXClick: () => void;
  onOutsideClick: () => void;
  onEscKeyDown: () => void;
  template: Template;
  index?: number;
  isMediaLoadDeferred?: boolean;
  categorySlug?: string;
}

const SOURCE_WIDE_THUMBNAIL_SIZE = { width: 1920, height: 1080 };
const SOURCE_THUMBNAIL_SIZE = { width: 1920, height: 1474 };
export const BREAKPOINTS_CONFIG: BreakpointConfig[] = [
  {
    maxScreenWidth: 375,
    imageWidth: 307,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 833,
    imageWidth: 765,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1023,
    imageWidth: 201,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1439,
    imageWidth: 283,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 2560,
    imageWidth: 341,
    densityList: [1, 2],
  },
];

export const WIDE_BREAKPOINTS_CONFIG: BreakpointConfig[] = [
  {
    maxScreenWidth: 375,
    imageWidth: 307,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 833,
    imageWidth: 765,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1023,
    imageWidth: 656,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 1439,
    imageWidth: 599,
    densityList: [1, 2],
  },
  {
    maxScreenWidth: 2560,
    imageWidth: 717,
    densityList: [1, 2],
  },
];

export const QuickViewModal: FC<QuickViewModalProps> = ({
  isOpen,
  onButtonXClick,
  onOutsideClick,
  onEscKeyDown,
  template,
  index,
  isMediaLoadDeferred,
  categorySlug,
}) => {
  const breakpoints = createBreakpoints(
    BREAKPOINTS_CONFIG,
    SOURCE_THUMBNAIL_SIZE,
  );
  const wideBreakpoints = createBreakpoints(
    WIDE_BREAKPOINTS_CONFIG,
    SOURCE_WIDE_THUMBNAIL_SIZE,
  );
  const { title, shortDescription, features, inspirationalAssetsV2 } = template;

  const dialogRef = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();
  const tabName = 'quick view';

  useLayoutEffect(() => {
    const dialog = dialogRef.current;

    const handleOutsideClick: EventListener = (e) => {
      if (dialogRef.current !== e.target) {
        return;
      }
      onOutsideClick();
    };

    const handleEscKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscKeyDown();
      }
    };

    if (dialog) {
      dialog.addEventListener('click', handleOutsideClick);
      dialog.addEventListener('keydown', handleEscKeyDown);

      return () => {
        dialog.removeEventListener('click', handleOutsideClick);
        dialog.removeEventListener('keydown', handleEscKeyDown);
      };
    }
  }, [onEscKeyDown, onOutsideClick]);

  useLayoutEffect(() => {
    if (isOpen) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [isOpen]);

  return (
    <dialog ref={dialogRef} className={s.dialog}>
      <div className={s.grid}>
        <div className={s.textCell}>
          <div className={s.tag}>
            {t('app.templates.inspirational.quickView.tag')}
          </div>
          <div className={s.title}>{title}</div>
          <div className={s.description}>{shortDescription}</div>
          <div className={s.features}>
            <div className={s.featuresTitle}>
              {t('app.templates.inspirational.quickView.featuresTitle')}
            </div>
            <div className={s.featuresList}>{features.join(' / ')}</div>
          </div>
          <div className={s.buttons}>
            <EditButton
              skin="light"
              template={template}
              categorySlug={categorySlug}
              index={index}
              className={s.editButton}
              tabName={tabName}
            />
            <ViewButton
              skin="light"
              template={template}
              categorySlug={categorySlug}
              index={index}
              className={s.viewButton}
              tabName={tabName}
            />
          </div>
        </div>
        {inspirationalAssetsV2.map((asset, assetIndex) => (
          <div className={s.thumbnailCell} key={asset.url}>
            {asset.type === InspirationalTemplateAssetType.VIDEO ? (
              <QuickViewVideo
                src={asset.url}
                className={s.video}
                isLoadDeferred={isMediaLoadDeferred}
              />
            ) : (
              <Picture
                isLazyLoad
                imagePath={asset.url || ''}
                sourceImageSize={
                  assetIndex === 0
                    ? SOURCE_WIDE_THUMBNAIL_SIZE
                    : SOURCE_THUMBNAIL_SIZE
                }
                breakpoints={assetIndex === 0 ? wideBreakpoints : breakpoints}
                alt={t('templates.img.altText', template.title)}
                imageClassName={s.image}
              />
            )}
          </div>
        ))}
      </div>
      <div className={s.header}>
        <button
          className={s.closeButton}
          aria-label="Close"
          onClick={onButtonXClick}
        >
          <Dismiss className={s.closeIcon} />
        </button>
      </div>
    </dialog>
  );
};
